export type TaskStatus = 'pending' | 'ongoing' | 'done';

export interface ScheduleTask {
  badge: string;
  badgeColor: string;
  name: string;
  desc: string;
}

export interface ScheduleEvent {
  time: string;
  title: string;
  icon: string;
  tasks: ScheduleTask[];
}

export interface ScheduleDay {
  id: string;
  label: string;
  title: string;
  theme: string;
  icon: string;
  color: string;
  events: ScheduleEvent[];
}

export interface CustomTask {
  id: string;
  dayId: string;
  title: string;
  desc: string;
  badge: string;
  createdAt: number;
  status?: TaskStatus;
}

export const STATUS_CONFIG: Record<TaskStatus, { label: string; icon: string; color: string; bg: string; nextStatus: TaskStatus }> = {
  pending:  { label: '未開始', icon: 'ri-time-line',            color: '#555',    bg: '#1a1a1a',     nextStatus: 'ongoing' },
  ongoing:  { label: '進行中', icon: 'ri-loader-4-line',        color: '#d4a843', bg: '#d4a84320',   nextStatus: 'done'    },
  done:     { label: '已完成', icon: 'ri-checkbox-circle-fill', color: '#7ec8a0', bg: '#7ec8a020',   nextStatus: 'pending' },
};

export function buildEventKey(dayId: string, eventIdx: number): string {
  return `${dayId}-${eventIdx}`;
}

export function buildTaskKey(dayId: string, eventIdx: number, taskIdx: number): string {
  return `${dayId}-${eventIdx}-${taskIdx}`;
}

// Special key for the hidden secret angel reveal task
export const MISSION_TASK_KEY = 'day1-4-mission';

// Special key for the hidden memory mission (微光記憶：民宿密令)
export const HIDDEN_MISSION_KEY = 'hidden-mission-glow-memory';

// The hidden mission event data (NOT in events array to avoid index shifting)
export const HIDDEN_MISSION_EVENT: ScheduleEvent = {
  time: '待解鎖',
  title: '【隱藏任務】微光記憶：民宿密令',
  icon: 'ri-eye-close-line',
  tasks: [
    { badge: '隱藏', badgeColor: 'bg-[#c06b9a]/20 text-[#c06b9a]', name: '微光記憶：民宿密令', desc: '時光檔案室發生了意外，所有的記憶被壓縮成一張張微小的碎片，散落在民宿各處。兩支隊伍必須在 40 分鐘內，儘可能蒐集所有的微光碎片，同時擅用各自的腳色能力，獲得勝利！' },
  ],
};

// Day1 hidden mission should appear after index 1 (超級採購王), before index 2 (黑白煮大廚)
export const HIDDEN_MISSION_INSERT_AFTER_DAY = 'day1';
export const HIDDEN_MISSION_INSERT_AFTER_IDX = 1;

export const scheduleDays: ScheduleDay[] = [
  {
    id: 'day0',
    label: 'Day 0',
    title: '【序章】重逢與共感之夜',
    theme: '卸下武裝，找回共鳴',
    icon: 'ri-moon-fill',
    color: '#7b68ee',
    events: [
      {
        time: '19:00 - 21:30',
        title: '靈魂晚宴：味覺記憶的索引',
        icon: 'ri-restaurant-line',
        tasks: [
          { badge: '任務', badgeColor: 'bg-[#e05555]/20 text-[#e05555]', name: '味覺記憶卡', desc: '點餐前，每人抽一張關鍵字卡（如：熬夜、雨天、遺憾），分享一段與成員共同經歷的故事。' },
        ],
      },
      {
        time: '21:30 - 23:00',
        title: '預熱相見歡：誰是偽裝者',
        icon: 'ri-spy-line',
        tasks: [
          { badge: '互動', badgeColor: 'bg-[#7b68ee]/20 text-[#7b68ee]', name: '兩真一假', desc: '使用我要說話功能打出三個關於自己兩真一假的秘密，由敵對猜測真假。' },
        ],
      },
    ],
  },
  {
    id: 'day1',
    label: 'Day 1',
    title: '【主線】飢餓生存戰',
    theme: '挑戰團體凝聚力，找回遺失的回憶碎片',
    icon: 'ri-sword-fill',
    color: '#e05555',
    events: [
      {
        time: '10:00 - 12:00',
        title: '階段一：老街尋寶（回憶採集）',
        icon: 'ri-map-pin-line',
        tasks: [
          { badge: '任務', badgeColor: 'bg-[#d4a843]/20 text-[#d4a843]', name: '片場人生', desc: '於時間內完成指定影片拍攝，並上傳至IG。' },
          { badge: '獎勵', badgeColor: 'bg-[#7ec8a0]/20 text-[#7ec8a0]', name: '食材優先挑選權', desc: '贏家獲得下午採購的「食材優先挑選權」。' },
        ],
      },
      {
        time: '13:30 - 15:30',
        title: '階段二：超級採購王（預算博弈）',
        icon: 'ri-shopping-bag-line',
        tasks: [
          { badge: '機制', badgeColor: 'bg-[#e05555]/20 text-[#e05555]', name: '陷阱食材', desc: '各組抽選預算與「陷阱食材」，在限時內買齊四道料理（前菜、主菜、甜點、湯品）所需材料。' },
        ],
      },
      {
        time: '16:00 - 21:00',
        title: '階段三：黑白煮大廚（逆境重生）',
        icon: 'ri-knife-line',
        tasks: [
          { badge: '主題', badgeColor: 'bg-[#d4a843]/20 text-[#d4a843]', name: '我們的友誼', desc: '料理主題以「我們的友誼」為核心，「故事感」佔分最高。' },
        ],
      },
      {
        time: '21:00 - 22:00',
        title: '階段四：深度與瘋狂',
        icon: 'ri-run-line',
        tasks: [
          { badge: '競賽', badgeColor: 'bg-[#7b68ee]/20 text-[#7b68ee]', name: '趣味競賽', desc: '進行 2-3 個考驗體力與默契的小遊戲（如：蒙眼障礙賽），歡笑與汗水並存！' },
        ],
      },
      {
        time: '22:00 - 24:00',
        title: '閨蜜夜話：真心話修復',
        icon: 'ri-candle-line',
        tasks: [
          { badge: '儀式', badgeColor: 'bg-[#7ec8a0]/20 text-[#7ec8a0]', name: '感謝告白', desc: '微光下進行「感謝告白」，結算兩天總積分，頒發圓夢獎勵金。' },
        ],
      },
    ],
  },
  {
    id: 'day2',
    label: 'Day 2',
    title: '【支線】友誼小山',
    theme: '不分你我，共登巔峰',
    icon: 'ri-mountain-fill',
    color: '#7ec8a0',
    events: [],
  },
];
